/** Legacy attachment reference — relative path or absolute URL, any host. */
const LEGACY_ATTACHMENT = /\/api\/v1\/attachments\/[a-f0-9]{24}\b/i;

/** The pieces of a person needed to resolve their avatar. */
export interface AvatarOwner {
  readonly _id?: string | undefined;
  readonly avatar?: string | null | undefined;
}

/**
 * Resolves a person's stored avatar value to a loadable URL.
 *
 * Legacy records hold V1 attachment references (relative or absolute) that no
 * longer resolve anywhere public — the legacy host requires auth. Those route
 * to the V3 avatar endpoint keyed by the *user*, which streams the photo
 * without the event-document visibility gate. Requesting it relative keeps
 * the call same-origin, so the SPA's `/api` proxy carries the session cookie.
 * New uploads are absolute S3 URLs and pass through. Default placeholder
 * avatars (`/assets/icons/avatar/…`) are treated as no avatar at all, so
 * initials render instead of a stock icon.
 *
 * @param owner - The person's id and stored avatar value.
 * @returns A loadable URL, or null when there is no usable avatar.
 */
export function avatarSrc(owner: AvatarOwner): string | null {
  const { _id, avatar } = owner;
  if (!avatar) return null;
  if (avatar.includes('/assets/icons/avatar/')) return null;
  // ?v busts caches poisoned by an early build that served cacheable errors.
  if (LEGACY_ATTACHMENT.test(avatar)) return _id ? `/api/v3/users/${_id}/avatar?v=2` : null;
  if (/^https?:\/\//i.test(avatar)) return avatar;
  return null;
}
